import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import ActivityPreviewComponent from './ActivityPreview';
import AdvertPreviewComponent from './AdvertPreview';
import TagsPreviewComponent from './TagsPreview';
import ClientQuickViewComponent from './ClientQuickView';
import ContactQuickViewComponent from './ContactQuickView';
import JobQuickViewComponent from './JobQuickView';
import OpportunityQuickViewComponent from './OpportunityQuickView';
import CandidateQuickViewComponent from './CandidateQuickView';
import SiteQuickViewComponent from './SiteQuickView';
import PlacementQuickViewComponent from './PlacementQuickView';
import MeetingAttendeesQuickView from './MeetingAttendeesQuickView';
import { GetSettingBySettingName } from 'services/UsersService';
import MessageQuickViewComponent from './MessageQuickView';
import DocumentQuickView from './DocumentQuickView';

interface Props {
  entityType: PreviewEntityType | '';
  recordId: number;
  open: boolean;
  isTagsPreview?: boolean;
  titleOverride?: string;
  showDelayMs?: number;
  hideDelayMs?: number;
  kioskToken: string | null;
}

export default function PreviewLoaderComponent({
  entityType,
  recordId,
  open: openProp,
  isTagsPreview = false,
  titleOverride = '',
  showDelayMs = 1750,
  hideDelayMs = 1250,
  kioskToken = null,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isQuickViewsEnabled, setIsQuickViewsEnabled] = useState(true);
  const [wasClicked, setWasClicked] = useState(false);

  useEffect(() => {
    const getSettingData = async () => {
      const res = await GetSettingBySettingName('QuickviewsEnabled');
      if (res && res !== 'True') setIsQuickViewsEnabled(false);
    };
    if (kioskToken === null) {
      getSettingData();
    }
  }, [kioskToken]);

  useEffect(() => {
    if (openProp && isQuickViewsEnabled) {
      const showPreviewTimeout = setTimeout(() => {
        setIsOpen(true);
      }, showDelayMs);
      return () => clearTimeout(showPreviewTimeout);
    }
  }, [openProp, showDelayMs, hideDelayMs, isQuickViewsEnabled]);

  const hideCallback = useCallback(() => {
    setIsOpen(false);
    setWasClicked(false);
  }, []);

  useEffect(() => {
    if (showDelayMs === 0) {
      setWasClicked(true);
    }
  }, [showDelayMs]);

  const preview = useMemo(() => {
    if (recordId === 0) return <></>;
    if (isTagsPreview) {
      let entityTypeId = 0;
      switch (entityType) {
        case 'activity':
          entityTypeId = 13;
          break;
        case 'client':
          entityTypeId = 1;
          break;
        case 'contact':
          entityTypeId = 2;
          break;
        case 'candidate':
          entityTypeId = 3;
          break;
        case 'opportunity':
          entityTypeId = 8;
          break;
        case 'placement':
          entityTypeId = 5;
          break;
        case 'advert':
          entityTypeId = 15;
          break;
        case 'job':
          entityTypeId = 4;
          break;
        case 'site':
          entityTypeId = 9;
          break;
      }
      if (entityTypeId === 0) return <></>;
      return (
        <TagsPreviewComponent
          entityTypeId={entityTypeId}
          recordId={recordId}
          recordName={titleOverride}
          isSourceHovered={openProp}
          hideDelayMs={hideDelayMs}
          hideCallback={hideCallback}
        />
      );
    }
    switch (entityType) {
      case 'activity':
        return (
          <ActivityPreviewComponent
            isSourceHovered={openProp}
            recordId={recordId}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'activity-message':
        return (
          <MessageQuickViewComponent
            isSourceHovered={openProp}
            recordId={recordId}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'client':
        return (
          <ClientQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'contact':
        return (
          <ContactQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'candidate':
        return (
          <CandidateQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'opportunity':
        return (
          <OpportunityQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'placement':
        return (
          <PlacementQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'advert':
        return (
          <AdvertPreviewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'job':
        return (
          <JobQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'site':
        return (
          <SiteQuickViewComponent
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'meeting-attendees':
        return (
          <MeetingAttendeesQuickView
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
      case 'candidate-resume':
        return (
          <DocumentQuickView
            source="candidate-resume"
            recordId={recordId}
            isSourceHovered={openProp}
            hideDelayMs={hideDelayMs}
            hideCallback={hideCallback}
          />
        );
    }
    return <></>;
  }, [entityType, recordId, isTagsPreview, openProp, hideCallback, hideDelayMs, titleOverride]);

  const clickOnlyPreview = useMemo(() => {
    if (isQuickViewsEnabled || !wasClicked) return null;
    if (entityType === 'candidate-resume') {
      return (
        <DocumentQuickView
          source="candidate-resume"
          recordId={recordId}
          isSourceHovered
          hideCallback={hideCallback}
        />
      );
    }
    return null;
  }, [isQuickViewsEnabled, entityType, recordId, wasClicked, hideCallback]);

  if (clickOnlyPreview) return clickOnlyPreview;

  return <>{isOpen && isQuickViewsEnabled && preview}</>;
}
